import { state } from "../../state/workspaceStore.js";
import React, { isValidElement, cloneElement, Children } from "react";
import { actionIcon } from "../icons/ActionIcon.jsx";
import { flatten } from "../common/reactHelpers.jsx";
import { pageWindow } from "../../utils/navigation.js";
export function resetTablePages() {
  for (const [key, paginationState] of state.tableStates)
    if (key.startsWith(state.current.route + "|")) paginationState.page = 1;
}
export function actionButtons(node) {
  if (!isValidElement(node)) return node;
  const p = node.props;
  if (node.type === "button") {
    const route = p["data-nav"],
      kind = p["data-confirm"],
      type = kind?.startsWith("delete")
        ? "delete"
        : kind?.startsWith("archive")
          ? "archive"
          : route
            ? route.includes("edit")
              ? "edit"
              : "view"
            : Object.keys(p).some((k) => k.includes("archive"))
              ? "archive"
              : Object.keys(p).some((k) => k.includes("delete"))
                ? "delete"
                : null;
    if (type) {
      const recordId =
        p["data-id"] ||
        p["data-archive-product"] ||
        p["data-delete-coupon"] ||
        p["data-archive-collection"];
      const record = [
        ...state.db.products,
        ...state.db.collections,
        ...state.db.customers,
        ...state.db.orders,
        ...state.db.payments,
        ...state.db.coupons,
      ].find((r) => r.id === recordId);
      const label =
        type[0].toUpperCase() +
        type.slice(1) +
        " " +
        (record?.name || record?.code || record?.id || "record");
      return cloneElement(
        node,
        {
          className:
            "table-action " +
            (["delete", "archive"].includes(type) ? "destructive" : ""),
          "aria-label": label,
          title: label,
        },
        actionIcon(type),
      );
    }
  }
  return cloneElement(node, {}, Children.map(p.children, actionButtons));
}
export function RecordTable({ table, tableKey, paginate = true }) {
  table = actionButtons(table);
  if (!paginate) return <div className="table-wrap">{table}</div>;
  const sections = flatten(table.props.children),
    body = sections.find((el) => el.type === "tbody");
  const all = flatten(body?.props.children),
    rows = all.filter(
      (row) =>
        isValidElement(row) &&
        row.type === "tr" &&
        !flatten(row.props.children).some(
          (c) => c.props?.className === "empty",
        ),
    );
  const paginationState = state.tableStates.get(tableKey) || {
      page: 1,
      size: 10,
    },
    result = pageWindow(rows.length, paginationState);
  Object.assign(paginationState, result);
  state.tableStates.set(tableKey, paginationState);
  const shown = rows.slice(result.start, result.end);
  const newBody = cloneElement(
    body,
    {},
    shown.length
      ? shown
      : all.find((r) =>
          flatten(r.props?.children).some(
            (c) => c.props?.className === "empty",
          ),
        ) || (
          <tr>
            <td colSpan={99} className="empty">
              No records.
            </td>
          </tr>
        ),
  );
  const pages = [
    ...new Set([
      1,
      result.pages,
      ...Array.from(
        {
          length: 5,
        },
        (_, i) => result.page + i - 2,
      ).filter((n) => n > 0 && n <= result.pages),
    ]),
  ].sort((a, b) => a - b);
  return (
    <>
      <div className="table-wrap">
        {cloneElement(
          table,
          {},
          sections.map((s, i) =>
            cloneElement(s.type === "tbody" ? newBody : s, {
              key: i,
            }),
          ),
        )}
      </div>
      <div className="pagination" role="group" aria-label="Table pagination">
        <span role="status">
          Showing {result.total ? result.start + 1 : 0}–{result.end} of{" "}
          {result.total}
        </span>
        <label>
          Rows per page{" "}
          <select
            key={result.size}
            defaultValue={result.size}
            data-page-size={tableKey}
            aria-label="Rows per page"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="page-buttons">
          <button
            type="button"
            data-page-key={tableKey}
            data-page={result.page - 1}
            disabled={result.page === 1}
          >
            Previous
          </button>
          {pages.map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && n > pages[i - 1] + 1 && <span>…</span>}
              <button
                type="button"
                data-page-key={tableKey}
                data-page={n}
                aria-current={n === result.page ? "page" : undefined}
                disabled={n === result.page}
                aria-label={"Page " + n}
              >
                {n}
              </button>
            </React.Fragment>
          ))}
          <button
            type="button"
            data-page-key={tableKey}
            data-page={result.page + 1}
            disabled={result.page === result.pages}
          >
            Next
          </button>
          <span>
            Page {result.page} of {result.pages}
          </span>
        </div>
      </div>
    </>
  );
}
export function tableViews(tree) {
  let index = 0;
  function visit(node) {
    if (!isValidElement(node)) return node;
    if (node.type === "div" && node.props.className === "table-wrap") {
      const table = flatten(node.props.children).find(
        (x) => x.type === "table",
      );
      if (table) {
        const key =
          state.current.route + "|" + (state.current.id || "") + "|" + index++;
        return (
          <RecordTable
            key={key}
            tableKey={key}
            table={table}
            paginate={!["dashboard", "product"].includes(state.current.route)}
          />
        );
      }
    }
    return cloneElement(node, {}, Children.map(node.props.children, visit));
  }
  return visit(tree);
}
